import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Loader2, Lock } from 'lucide-react';
import { useImportSeries } from '../../hooks/useImportSeries';
import type { ResourceSermon } from '../../hooks/useSeriesDetail';

interface Props {
  sermons: ResourceSermon[];
  isImported: boolean;
}

export function SeriesSermonList({ sermons, isImported }: Props) {
  const navigate = useNavigate();
  const { importSingleSermon } = useImportSeries();
  const [importingId, setImportingId] = useState<string | null>(null);
  const [importedIds, setImportedIds] = useState<Set<string>>(new Set());

  const handleOpen = async (sermon: ResourceSermon) => {
    setImportingId(sermon.id);
    try {
      const newSermonId = await importSingleSermon(sermon.id);
      setImportedIds(prev => new Set([...prev, sermon.id]));
      navigate(`/dashboard/editor/${newSermonId}`);
    } catch (err) {
      console.error('Failed to import sermon:', err);
    } finally {
      setImportingId(null);
    }
  };

  return (
    <div className="space-y-2">
      {sermons.map(sermon => (
        <div
          key={sermon.id}
          className="flex items-center justify-between gap-4 px-4 py-3 rounded-lg border border-sand/20 dark:border-slate/20 hover:border-electric/30 transition-colors"
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-sm font-bold text-electric w-6 shrink-0 text-center">{sermon.sermon_order}</span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-midnight dark:text-soft-white truncate">{sermon.title}</p>
              {sermon.scripture && (
                <p className="text-xs text-slate dark:text-soft-white/50 truncate">{sermon.scripture}</p>
              )}
            </div>
          </div>

          {isImported ? (
            <button
              onClick={() => handleOpen(sermon)}
              disabled={!!importingId}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-electric/40 text-electric rounded-lg hover:bg-electric/5 disabled:opacity-50 shrink-0"
            >
              {importingId === sermon.id
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <ExternalLink className="h-3.5 w-3.5" />
              }
              {importedIds.has(sermon.id) ? 'Open Again' : 'Open in Editor'}
            </button>
          ) : (
            <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate/40 dark:text-soft-white/25 shrink-0">
              <Lock className="h-3.5 w-3.5" />
              Import to unlock
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
